import DateTimePickerWrapper from '@components/system/CustomDateTimePicker/DateTimePickerWrapper';
import TimePickerWrapper from '@components/system/CustomDateTimePicker/TimePickerWrapper';
import {
  Button,
  FormControlLabel,
  Grid,
  Input,
  Radio,
  RadioGroup,
  // Input,
  createStyles,
  makeStyles
} from '@material-ui/core';
import React, {useEffect, useState} from 'react';
import {FONT_PRIMARY_COLOR, FONT_SECONDARY_COLOR} from 'src/style/variable';
import {Trans, useTranslation} from 'react-i18next';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {VaccineConsentFormState} from '../../ScheduleAppointment/ScheduleAppointment';
import AddSignatureModal from '../AddSignatureModal/AddSignatureModal';
import {
  FLU_VACCINE_QUESTION_ANSWER,
  VACCINE_INFO_KEY_MAPPER
} from '@shared/constants/Mapping';
import {Vaccine} from '@shared/services/service-mappers/AppointmentSchedulingMapper';
import {getVaccineInformationFile} from '@shared/services/VaccinesService';
const TITLE_FONT_SIZE = '2rem';
const FONT_SIZE = '1.25rem';
const INPUT_FONT_SIZE = '1rem';
const consentFormStyles = makeStyles(() =>
  createStyles({
    consentFormWrapper: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      // maxHeight: '450px',
      fontSize: FONT_SIZE,
      color: FONT_SECONDARY_COLOR,
      padding: '1rem',
      '& p': {
        maxWidth: '62ch'
      },
      overflow: 'auto'
    },
    link: {
      fontSize: FONT_SIZE
    },
    formTitle: {
      color: FONT_PRIMARY_COLOR,
      marginBottom: '2rem',
      fontSize: TITLE_FONT_SIZE,
      maxWidth: '45ch'
    },
    vaccineRadioGroupWrapper: {
      '& .MuiFormControlLabel-root': {
        '& .MuiFormControlLabel-label': {
          fontSize: FONT_SIZE,
          color: FONT_SECONDARY_COLOR,
          maxWidth: '62ch'
        }
      },
      '& .MuiButtonBase-root': {
        padding: '0.25rem'
      },
      gap: '0.2rem'
    },
    formLabel: {
      marginBlock: '0.5rem',
      fontSize: INPUT_FONT_SIZE
    },
    formControl: {
      paddingBottom: 0,
      marginBottom: 0
    },
    consentFormHeader: {
      paddingBottom: '3rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      borderBottom: `1px solid`,
      marginBottom: '1.5rem',
      linHeight: '1.5rem'
    },
    highlightedUnderlinedText: {
      fontWeight: 'bold',
      textDecoration: 'underline'
    },
    highlightedText: {
      fontWeight: 'bold'
    },
    consentFormBody: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      paddingBottom: '1rem'
    },
    consentFormQuestionsWrapper: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      marginBottom: '2rem',
      maxWidth: '72ch'
    },
    consentFormList: {
      listStylePosition: 'inside',
      paddingLeft: '1rem'
    },
    signatureWrapper: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start'
    },
    signatureImage: {
      objectFit: 'contain',
      maxHeight: '8rem'
    },
    vaccineTitle: {
      display: 'block',
      marginBottom: '0.5rem'
    },
    formCompletedBy: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem',
      alignItems: 'center',
      marginBottom: '2rem'
    },
    formCompletedByInput: {
      fontSize: '1.2rem',
      color: FONT_SECONDARY_COLOR
    },
    downloadVaccineInformationSheetLink: {
      maxWidth: 'max-content !important'
    }
  })
);

export interface IFluConsentFormState {
  digitalSignatureURL: string;
  consentFormDate: Date;
  isSickToday: string | undefined;
  hasAllergyToIngredient: string | undefined;
  hasReactionToInfulenza: string | undefined;
  hasGuillianSyndrome: string | undefined;
  formCompletedBy: string;
}

interface IFluConsentFormProps extends IFluConsentFormState {
  updateConsentFormState: React.Dispatch<
    React.SetStateAction<VaccineConsentFormState>
  >;
  selectedVaccine: Vaccine;
}

const FluConsentForm = ({
  updateConsentFormState,
  selectedVaccine,
  ...fluConsentFormState
}: IFluConsentFormProps): JSX.Element => {
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();

  const [signatureState, setSignatureState] = useState({
    isModalOpen: false
  });
  const openSignatureModal = () => {
    setSignatureState(p => ({...p, isModalOpen: true}));
  };

  const closeSignatureModal = () => {
    setSignatureState(p => ({...p, isModalOpen: false}));
  };

  const createSignHandler = (imgURL: string) => {
    updateConsentFormState(p => ({
      ...p,
      [VACCINE_INFO_KEY_MAPPER.FLUARIX_QUADRIVALENT]: {
        ...p[VACCINE_INFO_KEY_MAPPER.FLUARIX_QUADRIVALENT],
        digitalSignatureURL: imgURL
      }
    }));
    closeSignatureModal();
  };

  useEffect(() => {
    updateConsentFormState(p => ({
      ...p,
      [VACCINE_INFO_KEY_MAPPER.FLUARIX_QUADRIVALENT]: {
        ...p[VACCINE_INFO_KEY_MAPPER.FLUARIX_QUADRIVALENT],
        consentFormDate: new Date()
      }
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownload = async (): Promise<void> => {
    try {
      getVaccineInformationFile({
        vaccine_type: selectedVaccine.vaccine_info_key
      }).then((res: any) => {
        const url = window.URL.createObjectURL(new Blob([res]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'flu_vaccine.pdf');
        document.body.appendChild(link);
        link.click();
      });
    } catch (error: any) {
      enqueueSnackbar(
        (error && error.message) || t('CLINICS.CLINIC_GROUP_CLINICS_ERROR'),
        SnackBarConfig('e')
      );
    }
  };

  const questions = [
    {
      label: t(
        'APPOINTMENTS_SCHEDULAR_PORTAL.FLU_CONSENT_FORM.QUESTIONS.QUESTION_1'
      ),
      key: 'isSickToday'
    },
    {
      label: t(
        'APPOINTMENTS_SCHEDULAR_PORTAL.FLU_CONSENT_FORM.QUESTIONS.QUESTION_2'
      ),
      key: 'hasAllergyToIngredient'
    },
    {
      label: t(
        'APPOINTMENTS_SCHEDULAR_PORTAL.FLU_CONSENT_FORM.QUESTIONS.QUESTION_3'
      ),
      key: 'hasReactionToInfulenza'
    },
    {
      label: t(
        'APPOINTMENTS_SCHEDULAR_PORTAL.FLU_CONSENT_FORM.QUESTIONS.QUESTION_4'
      ),
      key: 'hasGuillianSyndrome'
    }
  ] as const;

  const classes = consentFormStyles();

  return (
    <div className={classes.consentFormWrapper}>
      <div>
        <h1 className={classes.formTitle}>
          {t('APPOINTMENTS_SCHEDULAR_PORTAL.FLU_CONSENT_FORM.TITLE')}
        </h1>
        <div className={classes.consentFormHeader}>
          <p>
            <Trans
              i18nKey={'APPOINTMENTS_SCHEDULAR_PORTAL.FLU_CONSENT_FORM.INFO'}
              components={[
                <span className={classes.highlightedText} key="X" />
              ]}
            />
          </p>
        </div>
        <div className={classes.consentFormBody}>
          <p
            className={[
              'link-style',
              classes.link,
              classes.downloadVaccineInformationSheetLink
            ].join(' ')}
            onClick={handleDownload}>
            {t(
              'APPOINTMENTS_SCHEDULAR_PORTAL.FLU_CONSENT_FORM.CLICK_HERE_TO_DOWNLOAD_VACCINE_INFORMATION_SHEET'
            )}
          </p>
        </div>
      </div>

      <div className={classes.consentFormQuestionsWrapper}>
        {questions.map(question => (
          <Grid container key={question.key} spacing={2} alignItems="center">
            <Grid item sm={12} md>
              {question.label}
            </Grid>
            <Grid item sm={12} md="auto">
              <RadioGroup
                row
                name={question.key}
                value={fluConsentFormState[question.key]}
                className={classes.vaccineRadioGroupWrapper}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  event.persist();
                  updateConsentFormState(p => ({
                    ...p,
                    [VACCINE_INFO_KEY_MAPPER.FLUARIX_QUADRIVALENT]: {
                      ...p[VACCINE_INFO_KEY_MAPPER.FLUARIX_QUADRIVALENT],
                      [question.key]: event.target.value
                    }
                  }));
                }}>
                <FormControlLabel
                  value={t(FLU_VACCINE_QUESTION_ANSWER.YES)}
                  control={<Radio />}
                  label={t(FLU_VACCINE_QUESTION_ANSWER.YES)}
                />
                <FormControlLabel
                  value={t(FLU_VACCINE_QUESTION_ANSWER.NO)}
                  control={<Radio />}
                  label={t(FLU_VACCINE_QUESTION_ANSWER.NO)}
                />
                <FormControlLabel
                  value={t(FLU_VACCINE_QUESTION_ANSWER.DONT_KNOW)}
                  control={<Radio />}
                  label={t(FLU_VACCINE_QUESTION_ANSWER.DONT_KNOW)}
                />
              </RadioGroup>
            </Grid>
          </Grid>
        ))}
      </div>
      <div className={classes.formCompletedBy}>
        <p>
          {t(
            'APPOINTMENTS_SCHEDULAR_PORTAL.FLU_CONSENT_FORM.FORM_COMPLETED_BY'
          )}
          :{' '}
        </p>
        <Input
          placeholder=""
          inputProps={{'aria-label': 'Form Completed By'}}
          className={classes.formCompletedByInput}
          value={fluConsentFormState.formCompletedBy}
          onChange={event => {
            event.persist();
            updateConsentFormState(p => ({
              ...p,
              [VACCINE_INFO_KEY_MAPPER.FLUARIX_QUADRIVALENT]: {
                ...p[VACCINE_INFO_KEY_MAPPER.FLUARIX_QUADRIVALENT],
                formCompletedBy: event.target.value
              }
            }));
          }}
        />
      </div>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          {fluConsentFormState.digitalSignatureURL ? (
            <div className={classes.signatureWrapper}>
              <img
                src={fluConsentFormState.digitalSignatureURL}
                className={classes.signatureImage}
              />
              <Button
                onClick={() =>
                  updateConsentFormState(p => ({
                    ...p,
                    [VACCINE_INFO_KEY_MAPPER.FLUARIX_QUADRIVALENT]: {
                      ...p[VACCINE_INFO_KEY_MAPPER.FLUARIX_QUADRIVALENT],
                      digitalSignatureURL: ''
                    }
                  }))
                }>
                {t('APPOINTMENTS_SCHEDULAR_PORTAL.REMOVE')}
              </Button>
            </div>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={openSignatureModal}>
              {t('APPOINTMENTS_SCHEDULAR_PORTAL.ADD_SIGNATURE')}
            </Button>
          )}
        </Grid>

        <Grid item xs={6} md={3}>
          <DateTimePickerWrapper
            inputVariant="standard"
            disabled
            value={fluConsentFormState.consentFormDate}
            onChange={() => {
              /**/
            }}
          />
          <p className={classes.formLabel}>
            {t('APPOINTMENTS_SCHEDULAR_PORTAL.FLU_CONSENT_FORM.DATE')}
          </p>
        </Grid>
        <Grid item xs={6} md={3}>
          <TimePickerWrapper
            disabled
            inputVariant="standard"
            value={fluConsentFormState.consentFormDate}
            onChange={() => {
              /**/
            }}
          />
          <p className={classes.formLabel}>
            {t('APPOINTMENTS_SCHEDULAR_PORTAL.FLU_CONSENT_FORM.TIME')}
          </p>
        </Grid>
      </Grid>
      {signatureState.isModalOpen && (
        <AddSignatureModal
          isOpen={signatureState.isModalOpen}
          onClose={closeSignatureModal}
          onCreateSign={createSignHandler}
        />
      )}
    </div>
  );
};

export default FluConsentForm;
